/**
 * GuestChecklistsSection Component
 * Checklists management for guest project view
 */
import React, { useState } from 'react';
import { Card, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { CheckSquare, Plus, Trash2, Square, Check } from 'lucide-react';
import { ProjectChecklist, ChecklistItem } from '../../../types/projects';
import ConfirmationModal from '../../../Components/ConfirmationModal';

interface GuestChecklistsSectionProps {
    checklists: { [key: string]: ProjectChecklist } | undefined;
    externalUserId: string;
    onCreateChecklist: (title: string, items?: string[]) => Promise<void>;
    onAddChecklistItem: (checklistKey: string, text: string) => Promise<void>;
    onSetItemCompleted: (checklistKey: string, itemKey: string, completed: boolean) => Promise<void>;
    onDeleteChecklistItem: (checklistKey: string, itemKey: string) => Promise<void>;
    onDeleteChecklist: (checklistKey: string) => Promise<void>;
    submitting: boolean;
    canDelete: (authorId: string) => boolean;
    /** Completed project: everything is shown, nothing can be added or changed. */
    readOnly?: boolean;
}

const MAX_TITLE_LENGTH = 200;
const MAX_ITEM_LENGTH = 500;

interface DeleteConfirmState {
    show: boolean;
    type: 'item' | 'checklist' | null;
    checklistKey: string | null;
    itemKey: string | null;
}

const GuestChecklistsSection: React.FC<GuestChecklistsSectionProps> = ({
    checklists,
    externalUserId,
    onCreateChecklist,
    onAddChecklistItem,
    onSetItemCompleted,
    onDeleteChecklistItem,
    onDeleteChecklist,
    submitting,
    canDelete,
    readOnly = false,
}) => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [addingToChecklist, setAddingToChecklist] = useState<string | null>(null);
    const [newItemText, setNewItemText] = useState('');
    const [togglingItem, setTogglingItem] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
        show: false,
        type: null,
        checklistKey: null,
        itemKey: null,
    });

    const checklistsArray = checklists
        ? Object.entries(checklists).map(([key, checklist]) => ({ ...checklist, key }))
        : [];

    const handleCreateChecklist = async () => {
        if (!newTitle.trim()) return;
        await onCreateChecklist(newTitle.trim());
        setNewTitle('');
        setShowCreateForm(false);
    };

    const handleAddItem = async (checklistKey: string) => {
        if (!newItemText.trim()) return;
        await onAddChecklistItem(checklistKey, newItemText.trim());
        setNewItemText('');
        setAddingToChecklist(null);
    };

    const handleToggleItem = async (checklistKey: string, itemKey: string, currentCompleted: boolean) => {
        setTogglingItem(itemKey);
        try {
            await onSetItemCompleted(checklistKey, itemKey, !currentCompleted);
        } finally {
            setTogglingItem(null);
        }
    };

    const handleDeleteItemClick = (checklistKey: string, itemKey: string) => {
        setDeleteConfirm({
            show: true,
            type: 'item',
            checklistKey,
            itemKey,
        });
    };

    const handleDeleteChecklistClick = (checklistKey: string) => {
        setDeleteConfirm({
            show: true,
            type: 'checklist',
            checklistKey,
            itemKey: null,
        });
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirm.type === 'item' && deleteConfirm.checklistKey && deleteConfirm.itemKey) {
            await onDeleteChecklistItem(deleteConfirm.checklistKey, deleteConfirm.itemKey);
        } else if (deleteConfirm.type === 'checklist' && deleteConfirm.checklistKey) {
            await onDeleteChecklist(deleteConfirm.checklistKey);
        }
        setDeleteConfirm({ show: false, type: null, checklistKey: null, itemKey: null });
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm({ show: false, type: null, checklistKey: null, itemKey: null });
    };

    const getItemsArray = (items: { [key: string]: ChecklistItem } | undefined) => {
        if (!items) return [];
        return Object.entries(items).map(([key, item]) => ({ ...item, key }));
    };

    const getDeleteMessage = () => {
        if (deleteConfirm.type === 'checklist') {
            return 'Vill du ta bort hela checklistan?';
        }
        return 'Vill du ta bort denna punkt?';
    };

    const getDeleteTitle = () => {
        if (deleteConfirm.type === 'checklist') {
            return 'Ta bort checklista';
        }
        return 'Ta bort punkt';
    };

    return (
        <>
            <Card className="guest-checklists-section mb-4">
                <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <CheckSquare size={18} />
                        <span>Checklistor</span>
                        {checklistsArray.length > 0 && (
                            <span className="badge bg-secondary">{checklistsArray.length}</span>
                        )}
                    </div>
                    {!showCreateForm && !readOnly && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowCreateForm(true)}
                            disabled={submitting}
                        >
                            <Plus size={16} className="me-1" />
                            Ny checklista
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    {showCreateForm && (
                        <div className="guest-checklists-section__create-form mb-3 p-3 bg-light rounded">
                            <Form.Control
                                type="text"
                                placeholder="Titel på checklista..."
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                maxLength={MAX_TITLE_LENGTH}
                                disabled={submitting}
                                autoFocus
                            />
                            <small className={`d-block text-end mt-1 ${newTitle.length >= MAX_TITLE_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                {newTitle.length}/{MAX_TITLE_LENGTH}
                            </small>
                            <div className="d-flex justify-content-end gap-2 mt-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        setNewTitle('');
                                    }}
                                    disabled={submitting}
                                >
                                    Avbryt
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleCreateChecklist}
                                    disabled={!newTitle.trim() || submitting}
                                >
                                    {submitting ? 'Skapar...' : 'Skapa'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {checklistsArray.length === 0 && !showCreateForm && (
                        <p className="text-muted text-center mb-0">
                            Inga checklistor ännu
                        </p>
                    )}

                    <div className="guest-checklists-section__list">
                        {checklistsArray.map((checklist) => {
                            const items = getItemsArray(checklist.items);
                            const completedCount = items.filter(i => i.completed).length;
                            const isOwner = !readOnly && canDelete(checklist.createdBy);

                            return (
                                <div
                                    key={checklist.key}
                                    className="guest-checklists-section__checklist border rounded mb-3"
                                >
                                    <div className="p-3 bg-light d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{checklist.title}</strong>
                                            <span className="text-muted ms-2">
                                                ({completedCount}/{items.length})
                                            </span>
                                        </div>
                                        {isOwner && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteChecklistClick(checklist.key)}
                                                disabled={submitting}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        )}
                                    </div>
                                    <ListGroup variant="flush">
                                        {items.map((item) => (
                                            <ListGroup.Item
                                                key={item.key}
                                                className="d-flex align-items-center justify-content-between"
                                            >
                                                <div
                                                    className="d-flex align-items-center gap-2 flex-grow-1 cursor-pointer"
                                                    onClick={() => !readOnly && togglingItem !== item.key && handleToggleItem(checklist.key, item.key, item.completed)}
                                                    style={{ cursor: readOnly || togglingItem === item.key ? 'default' : 'pointer' }}
                                                >
                                                    {togglingItem === item.key ? (
                                                        <Spinner animation="border" size="sm" style={{ width: 20, height: 20 }} />
                                                    ) : item.completed ? (
                                                        <Check size={20} className="text-success" />
                                                    ) : (
                                                        <Square size={20} className="text-muted" />
                                                    )}
                                                    <span className={item.completed ? 'text-decoration-line-through text-muted' : ''}>
                                                        {item.text}
                                                    </span>
                                                </div>
                                                {!readOnly && canDelete(checklist.createdBy) && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="text-danger p-0"
                                                        onClick={() => handleDeleteItemClick(checklist.key, item.key)}
                                                        disabled={submitting}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                )}
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                    {addingToChecklist === checklist.key ? (
                                        <div className="p-2 border-top">
                                            <Form.Control
                                                type="text"
                                                placeholder="Ny punkt..."
                                                value={newItemText}
                                                onChange={(e) => setNewItemText(e.target.value)}
                                                maxLength={MAX_ITEM_LENGTH}
                                                disabled={submitting}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && newItemText.trim()) {
                                                        handleAddItem(checklist.key);
                                                    }
                                                    if (e.key === 'Escape') {
                                                        setAddingToChecklist(null);
                                                        setNewItemText('');
                                                    }
                                                }}
                                            />
                                            <small className={`d-block text-end mt-1 ${newItemText.length >= MAX_ITEM_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                                {newItemText.length}/{MAX_ITEM_LENGTH}
                                            </small>
                                            <div className="d-flex justify-content-end gap-2 mt-2">
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setAddingToChecklist(null);
                                                        setNewItemText('');
                                                    }}
                                                    disabled={submitting}
                                                >
                                                    Avbryt
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleAddItem(checklist.key)}
                                                    disabled={!newItemText.trim() || submitting}
                                                >
                                                    Lägg till
                                                </Button>
                                            </div>
                                        </div>
                                    ) : readOnly ? null : (
                                        <div className="p-2 border-top">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-primary p-0"
                                                onClick={() => setAddingToChecklist(checklist.key)}
                                                disabled={submitting}
                                            >
                                                <Plus size={14} className="me-1" />
                                                Lägg till punkt
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>

            <ConfirmationModal
                show={deleteConfirm.show}
                title={getDeleteTitle()}
                message={getDeleteMessage()}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={submitting}
            />
        </>
    );
};

export default GuestChecklistsSection;
